import * as React from "react"

import { cn } from "@/lib/utils"
import { Card } from "@/components/ui/card"
import { useTranslation } from "@/hooks/useTranslation"
import { msg, type Msg } from "@shared/i18n"

/**
 * Loading skeletons: the page's shape, drawn before its data arrives —
 * PropPay's (E1/E2, 2026-09-28).
 *
 * A route used to wait on a spinner in the middle of an empty page, which
 * then vanished and was replaced by a whole page: the eye had nothing to
 * settle on and the layout jumped the moment the data landed. A skeleton is
 * the layout with the content left out, so the real thing fills in where the
 * reader is already looking and nothing moves.
 *
 * Kept deliberately coarse. A skeleton that mimics every row is a second copy
 * of the page to keep in step; one that gets the regions right — the header,
 * the figures, the list — is enough to make the wait read as the page arriving.
 */

/**
 * One block: the ink at a whisper, with a slow band of light crossing it so a
 * wait reads as work in progress rather than a page that failed to draw.
 *
 * The band is its own element, driven by tailwindcss-animate's `enter`
 * keyframe — from `slide-in-from-left-full` (-100%) to the element's own
 * `transform` (+100%), repeated — so no new keyframe is needed. The rest
 * position is written as `[transform:translateX(100%)]`, NOT `translate-x-full`:
 * since Tailwind 4 that class is the separate `translate` property, which ADDS
 * to the keyframe's `transform` instead of being its end point, and the band
 * swept 0 → +100% — starting every pass on top of the block. Under
 * reduced motion it is not drawn at all (`motion-reduce:hidden`): the block
 * stays, still. The global reduced-motion backstop in index.css would end it
 * off the block anyway; hiding it says so directly.
 *
 * aria-hidden: a block is shape, not content. What a screen reader hears is
 * the composed skeleton's status line below.
 */
const BLOCK = "relative overflow-hidden rounded-md bg-foreground/[.07] dark:bg-foreground/[.06]"
const SHIMMER = cn(
  "pointer-events-none absolute inset-0 [transform:translateX(100%)]",
  "bg-gradient-to-r from-transparent via-white/55 to-transparent dark:via-foreground/[.06]",
  "animate-in slide-in-from-left-full repeat-infinite ease-in-out [animation-duration:1600ms]",
  "motion-reduce:hidden"
)

function Skeleton({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden="true"
      data-skeleton=""
      className={cn(BLOCK, className)}
      {...props}
    >
      <span className={SHIMMER} />
      {children}
    </div>
  )
}

/**
 * A line of text that has not arrived: a block sitting in the LINE BOX the
 * text will fill.
 *
 * A fixed-height block (`h-4`) is a guess at a line, and a wrong guess is the
 * jump this file exists to prevent — a 16px block where a 20px line lands moves
 * everything below it by 4px. This is an inline-block holding a no-break space,
 * so its height IS the line height of whatever type it sits in: put it inside
 * the same `text-body font-semibold` the real value will use and the two are
 * the same height by construction. The block is drawn inside that box, a
 * fifth of an em in from the top and bottom, the way the glyphs will be.
 *
 * Spans throughout, so it is valid inside a <p>, a <td> or a <button>.
 */
function SkeletonText({ className }: { className?: string }) {
  return (
    <span aria-hidden="true" data-skeleton="" className={cn("relative inline-block w-24 max-w-full align-top", className)}>
      {"\u00a0"}
      <span className={cn(BLOCK, "absolute inset-x-0 bottom-[.22em] top-[.22em] block rounded-xs")}>
        <span className={SHIMMER} />
      </span>
    </span>
  )
}

/**
 * A page that announces its own wait, once, for every skeleton inside it.
 * Components that carry their own status line (a dashboard widget's
 * "Loading <title>…") read this and stay quiet beneath it, so a dashboard of
 * sixteen widgets is one announcement rather than sixteen.
 */
const QuietSkeletons = React.createContext(false)

function LoadingRegion({ label, active, children }: { label: string; active: boolean; children: React.ReactNode }) {
  return (
    <QuietSkeletons.Provider value={active}>
      {active && <LoadingStatus label={label} />}
      {children}
    </QuietSkeletons.Provider>
  )
}

/** True inside a LoadingRegion that is announcing — draw the shape, say nothing. */
function useQuietSkeletons() {
  return React.useContext(QuietSkeletons)
}

/** A Badge/StatusChip that has not arrived: the chip's 18px box, its corner. */
function BadgeSkeleton({ className }: { className?: string }) {
  return <Skeleton className={cn("h-[18px] w-14 shrink-0 rounded-full", className)} />
}

/**
 * The one announcement for a page drawn in its real layout with skeletons in
 * its data slots. Every block is aria-hidden, so without this a screen reader
 * hears nothing while a page loads. Render it only while loading; it says the
 * label once and is gone when the data lands.
 */
function LoadingStatus({ label }: { label: string }) {
  return (
    <span role="status" aria-busy="true" className="sr-only">
      {busyText(label)}
    </span>
  )
}

/**
 * The live region around a composed skeleton. Its label is real (sr-only)
 * TEXT, not an aria-label: every block inside is aria-hidden, and a status
 * region with no text content is announced as nothing by most screen readers.
 * `nested` drops the role, so a skeleton built from smaller skeletons is ONE
 * announcement, not one per part.
 */
/** The one line a composed skeleton announces — its own component, so a nested
    skeleton (which says nothing) does not pay for the translation hook. */
function BusyLabel({ label, fallback }: { label?: string; fallback?: Msg }) {
  const t = useTranslation()
  return <span className="sr-only">{busyText(label ?? t(fallback ?? LOADING))}</span>
}

const LOADING = msg("uiLoading", "Loading")

function Busy({
  label,
  fallback,
  nested,
  className,
  children,
  ...props
}: {
  label?: string
  /** Said when no `label` is passed. "Loading" when omitted. */
  fallback?: Msg
  nested?: boolean
  className?: string
  children: React.ReactNode
} & Omit<React.HTMLAttributes<HTMLDivElement>, "children">) {
  return (
    <div
      role={nested ? undefined : "status"}
      aria-busy={nested ? undefined : true}
      className={className}
      {...props}
    >
      {!nested && <BusyLabel label={label} fallback={fallback} />}
      {children}
    </div>
  )
}

/** "Loading contacts..." / "Loading" → "Loading contacts…" — one ellipsis, the character. */
export function busyText(label: string): string {
  return `${label.replace(/\s*(?:\.{2,}|…)\s*$/, "").trim() || "Loading"}…`
}

interface ComposedProps {
  /** What is loading, for a screen reader: "Loading contacts". */
  label?: string
  /** Inside another skeleton, which already announces the wait. */
  nested?: boolean
  className?: string
}

/** The eyebrow + title every page opens with. */
function HeadSkeleton({ avatar, className }: { avatar?: boolean; className?: string }) {
  return (
    <div aria-hidden="true" className={cn("mb-stack flex items-center gap-3.5", className)}>
      {avatar && <Skeleton className="size-12 shrink-0 rounded-full" />}
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-6 w-48 max-w-[70%]" />
      </div>
    </div>
  )
}

/** A list of rows in one card — registers, logs, queues. */
function ListSkeleton({
  rows = 6,
  lead = true,
  label,
  nested,
  bare,
  className,
}: ComposedProps & {
  rows?: number
  lead?: boolean
  /** Rows only, no card of their own — for a list that already sits inside one. */
  bare?: boolean
}) {
  const rowsEl = (
        <div className="divide-y divide-border/70">
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3.5">
              {lead && <Skeleton className="size-9 shrink-0 rounded-full" />}
              <div className="flex-1 space-y-2">
                <Skeleton className={cn("h-3.5", i % 3 === 0 ? "w-1/2" : i % 3 === 1 ? "w-2/3" : "w-2/5")} />
                <Skeleton className="h-3 w-1/3" />
              </div>
              <Skeleton className="h-4 w-16 shrink-0" />
            </div>
          ))}
        </div>
  )
  return (
    <Busy label={label} nested={nested} className={className}>
      {bare ? rowsEl : <Card className="overflow-hidden">{rowsEl}</Card>}
    </Busy>
  )
}

/**
 * A phone register (ui/record-list's RecordList of RecordRows), rows included:
 * the same card, the same 60px row, the same 16px inset, the lead at the size
 * the page passes, and each line in the type the real line uses — so a row of
 * blocks is exactly as tall as the row that replaces it. `menu` draws the ⋯
 * a row with actions carries; without it, the disclosure chevron.
 */
function RecordListSkeleton({
  rows = 8,
  lead = 36,
  leadShape = "circle",
  meta = true,
  detail = false,
  menu = true,
  trailing = false,
  label,
  nested,
  className,
}: ComposedProps & {
  rows?: number
  /** A figure at the row's trailing edge (a price), in `text-body`. */
  trailing?: boolean
  /** The lead's size in px, or 0 for none. */
  lead?: number
  leadShape?: "circle" | "tile"
  meta?: boolean
  /** A third line: `true` for text, `"chips"` for a line of status chips. */
  detail?: boolean | "chips"
  menu?: boolean
}) {
  return (
    <Busy label={label} nested={nested} className={className}>
      <Card className="overflow-hidden p-0">
        <ul aria-hidden="true" className="divide-y divide-border/70">
          {Array.from({ length: rows }, (_, i) => (
            <li key={i} className="flex min-h-16 items-center gap-1 pr-2">
              <div className={cn("flex min-h-16 min-w-0 flex-1 items-center gap-4 self-stretch py-3 pl-4", menu ? "pr-1" : "pr-3")}>
                {lead > 0 && (
                  <Skeleton
                    className={cn("shrink-0", leadShape === "circle" ? "rounded-full" : "rounded-md")}
                    style={{ width: lead, height: lead }}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-body font-semibold">
                    <SkeletonText className={i % 3 === 0 ? "w-3/5" : i % 3 === 1 ? "w-1/2" : "w-2/3"} />
                  </div>
                  {meta && (
                    <div className="text-label">
                      <SkeletonText className={i % 2 ? "w-2/5" : "w-1/2"} />
                    </div>
                  )}
                  {detail === "chips" ? (
                    <div className="mt-0.5 truncate text-caption">
                      <span className="inline-flex max-w-full items-center gap-1.5 whitespace-nowrap">
                        <BadgeSkeleton />
                        <BadgeSkeleton className="w-16" />
                      </span>
                    </div>
                  ) : detail ? (
                    <div className="mt-0.5 text-caption">
                      <SkeletonText className="w-1/3" />
                    </div>
                  ) : null}
                </div>
                {trailing && (
                  <span className="shrink-0 text-body font-semibold"><SkeletonText className="w-16" /></span>
                )}
                {!menu && <span className="size-4 shrink-0" />}
              </div>
              {menu && <span className="size-9 shrink-0" />}
            </li>
          ))}
        </ul>
      </Card>
    </Busy>
  )
}

/** A row of summary figures. */
function FiguresSkeleton({ n = 4, label, nested, className }: ComposedProps & { n?: number }) {
  return (
    <Busy label={label} nested={nested} className={cn("mb-stack grid grid-cols-2 gap-3 lg:grid-cols-4", className)}>
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className="space-y-2 rounded-md border border-border/60 px-4 py-3">
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-5 w-24" />
        </div>
      ))}
    </Busy>
  )
}

/** A table in its shell: a header strip over rows. Two columns on a phone. */
function TableSkeleton({
  rows = 8,
  columns = 5,
  label,
  nested,
  className,
}: ComposedProps & { rows?: number; columns?: number }) {
  const cols = Math.max(2, columns)
  const grid = cn(
    "grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4",
    "sm:[grid-template-columns:minmax(0,2fr)_repeat(var(--skeleton-cols),minmax(0,1fr))]"
  )
  const style = { "--skeleton-cols": cols - 1 } as React.CSSProperties
  return (
    <Busy label={label} nested={nested} className={className}>
      <Card className="overflow-hidden">
        <div className={cn(grid, "border-b border-border/70 bg-foreground/[.02] px-4 py-3")} style={style}>
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} className={cn("h-2.5", c === 0 ? "w-20" : "w-12", c > 1 && "hidden sm:block")} />
          ))}
        </div>
        <div className="divide-y divide-border/70">
          {Array.from({ length: rows }, (_, r) => (
            <div key={r} className={cn(grid, "items-center px-4 py-3.5")} style={style}>
              {Array.from({ length: cols }, (_, c) => (
                <Skeleton
                  key={c}
                  className={cn(
                    "h-3.5",
                    c === 0 ? (r % 2 ? "w-3/4" : "w-1/2") : "w-2/3",
                    c > 1 && "hidden sm:block"
                  )}
                />
              ))}
            </div>
          ))}
        </div>
      </Card>
    </Busy>
  )
}

/** A generic page: heading, then a list. What a route shows while it loads. */
function PageSkeleton({ label, className, ...props }: ComposedProps & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <Busy label={label} className={className} {...props}>
      <HeadSkeleton />
      <ListSkeleton rows={6} nested />
    </Busy>
  )
}

/** A settings section, or an admin tab: figures over a list. */
function SectionSkeleton({ label, figures = true, className }: ComposedProps & { figures?: boolean }) {
  return (
    <Busy label={label} className={className}>
      {figures && <FiguresSkeleton nested />}
      <ListSkeleton rows={5} nested />
    </Busy>
  )
}

/** The Dashboard: project header, a progress line, figures, then two cards. */
function DashboardSkeleton({ label, className }: Omit<ComposedProps, "nested">) {
  return (
    <Busy label={label} fallback={msg("uiLoadingDashboard", "Loading Dashboard")} className={className}>
      <HeadSkeleton avatar />
      <Card className="mb-stack space-y-3 p-card">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-2 w-full rounded-xs" />
      </Card>
      <FiguresSkeleton nested />
      <div className="grid grid-cols-1 gap-stack lg:grid-cols-2">
        {[0, 1].map((i) => (
          <Card key={i} className="space-y-3 p-card">
            <Skeleton className="h-5 w-40" />
            {[0, 1, 2].map((j) => (
              <Skeleton key={j} className="h-11 w-full" />
            ))}
          </Card>
        ))}
      </div>
    </Busy>
  )
}

/** The project picker: a grid of project cards. */
function ProjectGridSkeleton({ label, count = 6, className }: Omit<ComposedProps, "nested"> & { count?: number }) {
  return (
    <Busy
      label={label}
      fallback={msg("uiLoadingProjects", "Loading Projects")}
      className={cn("grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-stack", className)}
    >
      {Array.from({ length: count }, (_, i) => (
        <Card key={i} className="flex items-center gap-3.5 p-card">
          <Skeleton className="size-12 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </Card>
      ))}
    </Busy>
  )
}

export {
  Skeleton,
  SkeletonText,
  BadgeSkeleton,
  LoadingStatus,
  LoadingRegion,
  useQuietSkeletons,
  RecordListSkeleton,
  HeadSkeleton,
  ListSkeleton,
  FiguresSkeleton,
  TableSkeleton,
  PageSkeleton,
  SectionSkeleton,
  DashboardSkeleton,
  ProjectGridSkeleton,
}
